import AddIcon from "@mui/icons-material/Add"
import { Box, Grid, Typography } from "@mui/material"
import React, { useCallback, useContext, useState } from "react"
import { customPalette } from "../../../../../../../../../../../../theme"
import { BaseButton } from "../../../../../../../../../../../components/buttons/BaseButton"
import { FormNumberInput } from "../../../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { ErrorContext } from "../../../../../../../../../../../components/layout/error-snackbar"
import { stringToNumber } from "../../../../../../../../../../../core/services/helper-service"
import { BSBuildingContext } from "../../../../../components/context/BSBuildingContext"
import { BSParkingContext } from "../../../../BSBuildingParkingPage/context/BSParkingContext"
import { BSParkingListContext } from "../../../../BSBuildingParkingPage/context/BSParkingListContext"
import { BSPatioContext } from "../../../../BSPatioPage/context/BSPatioContext"
import { BSLevelContext } from "../../../context/BSLevelContext"
import { GroupLevel } from "../../../context/GroupLevel"
import { GroupLevelCard } from "./GroupLevelCard"
import { BSLevelFormContext } from "../../../context/BSLevelFormContext"

interface IProps {
  groupLevelList: GroupLevel[]
  isSuperstructure?: boolean
}

export function BSBuildingLevelContainer({ groupLevelList, isSuperstructure = false }: Readonly<IProps>): React.JSX.Element {
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const { setHasToControlPatio } = useContext(BSLevelContext)
  const { initializeBSBuildingGroupLevel } = useContext(BSLevelFormContext)
  const openErrorSnackbar = useContext(ErrorContext)
  const { infrastructureParkingList } = useContext(BSParkingListContext)
  const { setIsParkingFormOpen, setIsParkingFormForSuperstructure } = useContext(BSParkingContext)
  const { bsPatioList } = useContext(BSPatioContext)

  const [levelNumberToCreate, setLevelNumberToCreate] = useState<number>(0)

  function handleInitializeLevelNumber(event: React.ChangeEvent<HTMLInputElement>): void {
    setLevelNumberToCreate(stringToNumber(event.target.value))
  }

  const handlePatioControl: () => void = useCallback(() => {
    if (isSuperstructure && bsPatioList && bsPatioList.length > 0) {
      setHasToControlPatio(true)
    }
  }, [bsPatioList, isSuperstructure, setHasToControlPatio])

  const handleBSBuildingLevelInitialization: () => void = useCallback(() => {
    if (
      (isSuperstructure && levelNumberToCreate > 0) ||
      (!isSuperstructure && levelNumberToCreate > 0 && infrastructureParkingList.length > 0)
    ) {
      initializeBSBuildingGroupLevel(levelNumberToCreate, isSuperstructure)
      handlePatioControl()
    } else if (!isSuperstructure && levelNumberToCreate > 0 && infrastructureParkingList.length === 0) {
      setIsParkingFormForSuperstructure(isSuperstructure)
      setIsParkingFormOpen(true)
    } else {
      openErrorSnackbar(new Error("Veuillez entrer un nombre de niveaux valide à créer"))
    }
    setLevelNumberToCreate(0)
  }, [
    isSuperstructure,
    levelNumberToCreate,
    infrastructureParkingList.length,
    initializeBSBuildingGroupLevel,
    handlePatioControl,
    setIsParkingFormForSuperstructure,
    setIsParkingFormOpen,
    openErrorSnackbar,
  ])

  return (
    <Box
      border={1}
      borderRadius={3}
      p={3}
      borderColor={customPalette.paperBackground}
      boxShadow="0px 1px 3px rgba(0, 0, 0, 0.08)"
      width="100%"
      sx={{ backgroundColor: customPalette.white }}>
      {isSuperstructure ? (
        <Typography variant="h6" sx={{ textAlign: "center" }}>
          Superstructure
        </Typography>
      ) : (
        <Typography variant="h6" sx={{ textAlign: "center" }}>
          Infrastructure
        </Typography>
      )}

      <Typography variant="h6">Niveaux</Typography>
      <Typography>{`Renseignez les niveaux du bâtiment "${selectedBSBuilding?.name}" puis ajouter les zones correspondantes. Le nombre de niveaux définit la liste des usages.`}</Typography>

      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs={4}>
          <FormNumberInput
            fieldName="levelNumber"
            label="Nombre de niveaux"
            value={levelNumberToCreate}
            formBackgroundColor={customPalette.white}
            handleEventChange={handleInitializeLevelNumber}
          />
        </Grid>
        <Grid item xs={4}>
          <Box display="flex" justifyContent="flex-end">
            <BaseButton label="Ajouter un groupe de niveaux" onClick={handleBSBuildingLevelInitialization} icon={AddIcon} />
          </Box>
        </Grid>
      </Grid>

      {groupLevelList.map((groupLevel) => (
        <GroupLevelCard key={groupLevel.id} groupLevel={groupLevel} handlePatioControl={handlePatioControl} />
      ))}
    </Box>
  )
}
